import { LazyLoader, Modal } from '@bp/ui-components';
import { observer } from 'mobx-react-lite';
import { Suspense, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useAuthClaims } from '../../../hooks/useAuthClaims';
import { useLoadBasicData } from '../../../hooks/useLoadBasicData';
import { useUserConfigContext } from '../../../hooks/useUserConfigContext';
import { DeputateEpochForm } from '../Forms/DeputateEpochForm';
import { useDeputateLessons } from '../hooks/useDeputateLessons';
import { LessonFormClassType } from '../types';

type DeputateEpochModalProps = {
  lessonUuid: string | null;
  classUuid?: string;
  isOpen: boolean;
  onClose: () => void;
};

export const DeputateEpochModal = observer(({ lessonUuid, isOpen, onClose, classUuid }: DeputateEpochModalProps) => {
  const { t } = useTranslation();

  const [loading, setLoading] = useState<boolean>(false);
  const { pimAuthClaims } = useAuthClaims();
  const schoolYear = useUserConfigContext().selectedSchoolYear;
  const { classesData } = useLoadBasicData({ pause: false });

  const { queryLessonsData } = useDeputateLessons({
    organizationUuid: pimAuthClaims.getOrganizationUuid(),
    schoolYearUuid: schoolYear?.uuid ?? '',
  });

  const grade = classesData?.classes.find((c) => c.uuid === classUuid)?.grade;
  const lesson = queryLessonsData.lessons.find((lesson) => lesson.uuid === lessonUuid);
  const lessonClasses = lesson?.lessonClassesConnection.edges.map(({ node }) => {
    return {
      uuid: node.uuid,
      class: node.class,
      groups: node.groups,
    };
  });

  return (
    <Modal
      title={lessonUuid ? t('epochs.editPlan') : t('epochs.addPlan')}
      loading={loading}
      isOpen={isOpen}
      onRequestClose={onClose}
    >
      <Suspense fallback={<LazyLoader embedded forceHeight='40vh' />}>
        <DeputateEpochForm
          classData={classesData?.classes}
          lessonUuid={lessonUuid}
          defaultInitialValues={{
            timetableEnabled: true,
            validGrades: grade ? [grade] : null,
            lessonClasses: lessonClasses ?? [],
          }}
          onLoad={setLoading}
          onClose={onClose}
          classUuid={classUuid}
        />
      </Suspense>
    </Modal>
  );
});
